import React from "react";
import {
  UseFormRegister,
  Control,
  Controller,
  FieldErrors,
  UseFieldArrayReturn,
} from "react-hook-form";
import { AlertCircle, Users, Plus, Trash2 } from "lucide-react";
import ProjectFormSection from "./ProjectFormSection";

interface ProjectFormSkillsProps {
  register: UseFormRegister<any>;
  control: Control<any>;
  errors: FieldErrors<any>;
  fieldArray: UseFieldArrayReturn<any, "skillsRequired", "id">;
  isActive: boolean;
  onToggle: () => void;
}

const ProjectFormSkills: React.FC<ProjectFormSkillsProps> = ({
  register,
  control,
  errors,
  fieldArray,
  isActive,
  onToggle,
}) => {
  const { fields, append, remove } = fieldArray;

  return (
    <ProjectFormSection
      id="skills"
      title="Skills Required"
      description="Define required and optional skills"
      icon={Users}
      iconGradient="from-blue-500 to-indigo-500"
      isActive={isActive}
      onToggle={onToggle}
      badge={`${fields.length} skill${fields.length !== 1 ? "s" : ""}`}
    >
      {(errors.skillsRequired as any)?.root && (
        <div className="p-2.5 sm:p-3 bg-red-50 border border-red-200 rounded-lg sm:rounded-xl flex items-center gap-2 text-red-600 text-xs sm:text-sm">
          <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
          {(errors.skillsRequired as any).root.message}
        </div>
      )}

      {fields.map((field, index) => (
        <div
          key={field.id}
          className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl group"
        >
          <div className="flex-1 min-w-0">
            <input
              {...register(`skillsRequired.${index}.name`)}
              type="text"
              placeholder="e.g., Critical Thinking"
              className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg sm:rounded-xl border-2 transition-all focus:outline-none ${
                (errors.skillsRequired as any)?.[index]?.name
                  ? "border-red-300 focus:border-red-500 bg-red-50"
                  : "border-gray-200 focus:border-[#8B52FF] focus:ring-2 focus:ring-[#8B52FF]/20 bg-white"
              }`}
            />
            {(errors.skillsRequired as any)?.[index]?.name && (
              <p className="mt-1.5 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                <span className="line-clamp-2">
                  {(errors.skillsRequired as any)[index]?.name?.message}
                </span>
              </p>
            )}
          </div>
          <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3">
            <Controller
              name={`skillsRequired.${index}.required`}
              control={control}
              render={({ field: checkboxField }) => (
                <label className="flex items-center gap-2 cursor-pointer px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={checkboxField.value}
                    onChange={(e) => checkboxField.onChange(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-[#8B52FF] focus:ring-[#8B52FF]"
                  />
                  <span className="text-xs sm:text-sm text-gray-600">
                    Required
                  </span>
                </label>
              )}
            />
            {fields.length > 1 && (
              <button
                type="button"
                onClick={() => remove(index)}
                className="p-1.5 sm:p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors sm:opacity-0 sm:group-hover:opacity-100"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={() => append({ name: "", required: true })}
        className="w-full p-2.5 sm:p-3 border-2 border-dashed border-gray-300 rounded-lg sm:rounded-xl text-gray-500 hover:border-[#8B52FF] hover:text-[#8B52FF] transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
      >
        <Plus className="w-4 h-4" />
        Add Skill
      </button>
    </ProjectFormSection>
  );
};

export default ProjectFormSkills;
